import { formatPickupWindowLabel } from "./formatPickupWindowLabel";
import { getDriveMinutesFromDeparture, getWalkDriveMinutes } from "./travelEstimates";
import { resolveDepartureIso } from "./travelDatetimeUtils";
import { parseTravelSlotFilterKey } from "./travelDatetimeUtils";
import {
  getDayScheduleStatus,
  weekdayKeyFromDate,
  yesterdayConfigFromDays,
} from "./dayServicePeriods";

const COMFORTABLE_WAIT_MIN = 20;
/** Drive customers see the slot end as the pickup time. */
const PICKUP_SLOT_MS = 5 * 60 * 1000;

/** Leave so the customer arrives at the pickup time. Drive uses the slot end. */
function leaveInstantForPickup(slotStart, driveMinutes, arriveAtSlotEnd = true) {
  const drive = Math.max(0, Number(driveMinutes) || 0);
  const target = slotStart.getTime() + (arriveAtSlotEnd ? PICKUP_SLOT_MS : 0);
  return new Date(target - drive * 60 * 1000);
}

export function parsePickupSlotInstant(pickupWindow, dateStr) {
  const hm = String(pickupWindow || "").match(/^(\d{1,2}):(\d{2})/);
  if (!hm) return null;
  const parts = String(dateStr || "").split("/").map(Number);
  if (parts.length < 3) return null;
  const [month, day, year] = parts;
  const d = new Date(year, month - 1, day, Number(hm[1]), Number(hm[2]), 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatTravelTimeLabel(date) {
  if (!date || Number.isNaN(date.getTime())) return "—";
  // Always 24h HH:mm — CI runners often use en-US (AM/PM) and must match product UI.
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** API / storage pickup window: always `HH:mm - HH:mm` (24h). */
export function formatPickupWindowHHMM(start, end) {
  if (!start || Number.isNaN(start.getTime()) || !end || Number.isNaN(end.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const a = `${pad(start.getHours())}:${pad(start.getMinutes())}`;
  const b = `${pad(end.getHours())}:${pad(end.getMinutes())}`;
  return `${a} - ${b}`;
}

const slotKey = (pickupWindow, date) =>
  pickupWindow && date ? `${pickupWindow}|${date}` : "";

function roundNearest5Min(date) {
  const d = new Date(date);
  let m = d.getMinutes();
  let h = d.getHours();
  const r = m % 5;
  if (r <= 2) m -= r;
  else m += 5 - r;
  if (m >= 60) {
    m -= 60;
    h += 1;
  }
  d.setHours(h, m, 0, 0);
  return d;
}

/** First 5-minute slot window at or after arrival (leave + drive). */
function roundDownTo5Min(date) {
  const d = new Date(date);
  const m = d.getMinutes();
  d.setMinutes(m - (m % 5), 0, 0);
  return d;
}

/** First 5-minute mark at or after this instant. An exact mark stays put. */
function roundUpTo5Min(date) {
  const five = 5 * 60 * 1000;
  return new Date(Math.ceil(date.getTime() / five) * five);
}

/**
 * Slot start the customer can still reach.
 * Drive arrives at the slot end, so a 04:56 arrival books 04:55–05:00.
 */
function slotStartAtOrAfterArrival(arrivalAt, arriveAtSlotEnd) {
  if (arriveAtSlotEnd) {
    const slotEnd = roundUpTo5Min(arrivalAt);
    return new Date(slotEnd.getTime() - PICKUP_SLOT_MS);
  }
  return roundUpTo5Min(arrivalAt);
}

/**
 * When the drive is longer than the time until the kitchen slot, that leave
 * time is already past. Move the slot to the later of food-ready and arrival.
 */
function pushSlotUntilLeaveIsReachable({
  foodReady,
  leaveBy,
  drive,
  prep,
  earliestLeave,
  arriveAtSlotEnd,
}) {
  if (!(earliestLeave instanceof Date) || Number.isNaN(earliestLeave.getTime())) return null;
  if (!leaveBy || leaveBy >= earliestLeave) return null;

  const earliestArrival = new Date(earliestLeave.getTime() + drive * 60 * 1000);
  let nextStart = slotStartAtOrAfterArrival(earliestArrival, arriveAtSlotEnd);
  if (foodReady && nextStart < foodReady) nextStart = new Date(foodReady.getTime());
  nextStart = rollAsapSlotPastCutoff(nextStart, prep, earliestLeave) || nextStart;

  let nextLeave = leaveInstantForPickup(nextStart, drive, arriveAtSlotEnd);
  let guard = 0;
  while (nextLeave < earliestLeave && guard < 48) {
    nextStart = new Date(nextStart.getTime() + PICKUP_SLOT_MS);
    nextLeave = leaveInstantForPickup(nextStart, drive, arriveAtSlotEnd);
    guard += 1;
  }

  let nextPrep = new Date(nextStart.getTime() - prep * 60 * 1000);
  if (nextPrep < earliestLeave) nextPrep = new Date(earliestLeave.getTime());

  return { foodReady: nextStart, prepStart: nextPrep, leaveBy: nextLeave };
}

function formatSlotDate(date) {
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

/**
 * Checkout timeline for takeaway and dine-in.
 * Same kitchen cutoff as drive (order by = slot start − prep) and the same
 * pickup clock (slot end). No leave-by or travel — the customer is already
 * coming to the restaurant.
 */
export function buildOnSiteCheckoutTiming({
  pickupWindow,
  pickupDate,
  prepMinutes,
  remainingOrders = null,
  orderType = "TAKEAWAY",
} = {}) {
  const slotStart = parsePickupSlotInstant(pickupWindow, pickupDate);
  if (!slotStart) return null;
  const parsedPrep = Number(prepMinutes);
  const prep = Number.isFinite(parsedPrep) && parsedPrep >= 0 ? parsedPrep : 10;
  const orderBeforeAt = new Date(slotStart.getTime() - prep * 60 * 1000);
  const slotEnd = new Date(slotStart.getTime() + PICKUP_SLOT_MS);
  const remaining = Number(remainingOrders);
  return {
    orderBeforeLabel: formatTravelTimeLabel(orderBeforeAt),
    prepMinutes: prep,
    driveMinutes: 0,
    leaveByLabel: null,
    slotLabel: `${formatTravelTimeLabel(slotStart)}\u2013${formatTravelTimeLabel(slotEnd)}`,
    pickupWindow: formatPickupWindowHHMM(slotStart, slotEnd),
    date: pickupDate,
    slotRemainingOrders: Number.isFinite(remaining) ? remaining : null,
    arrivalKind: String(orderType || "").toUpperCase() === "DINEIN" ? "dinein" : "pickup",
  };
}

/** Last bookable instant for a slot — mirrors backend edgeCutoff (slot start − prepMinutes). */
function computeSlotBookingCutoffAt(slotStart, prepMinutes) {
  const prep = Number(prepMinutes) || 10;
  if (!slotStart || Number.isNaN(slotStart.getTime())) return null;
  return new Date(slotStart.getTime() - prep * 60 * 1000);
}

function minuteStartMs(date) {
  const d = new Date(date);
  d.setSeconds(0, 0);
  return d.getTime();
}

/** True when the kitchen can no longer accept orders for this slot (same rule as holdSlot). */
export function isSlotPastBookingCutoff(slotStart, prepMinutes, now = new Date()) {
  const cutoff = computeSlotBookingCutoffAt(slotStart, prepMinutes);
  if (!cutoff || Number.isNaN(now.getTime())) return false;
  return minuteStartMs(now) > minuteStartMs(cutoff);
}

/** Advance to the next 5-minute slot while the current one is past its booking cutoff. */
function rollAsapSlotPastCutoff(slotStart, prepMinutes, now = new Date()) {
  let start = slotStart instanceof Date ? new Date(slotStart.getTime()) : null;
  if (!start || Number.isNaN(start.getTime())) return null;
  while (isSlotPastBookingCutoff(start, prepMinutes, now)) {
    start = new Date(start.getTime() + 5 * 60 * 1000);
  }
  return start;
}

/**
 * Parse "HH:mm" opens-at label relative to reference.
 * If that clock time has already passed today (e.g. closed after 23:00, opens 11:00),
 * roll forward to the next calendar day so ASAP does not invent a midnight slot.
 */
export function parseOpensAtInstant(opensAtLabel, referenceDate) {
  const hm = String(opensAtLabel || "").match(/^(\d{1,2}):(\d{2})/);
  if (!hm) return null;
  const ref = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  if (Number.isNaN(ref.getTime())) return null;
  const d = new Date(ref);
  d.setHours(Number(hm[1]), Number(hm[2]), 0, 0);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getTime() <= ref.getTime()) {
    d.setDate(d.getDate() + 1);
  }
  return d;
}

/**
 * Next opening time (HH:mm) from restaurant info / schedule, for ASAP clamping.
 * Returns the upcoming service open when still closed (before lunch or in the afternoon gap).
 */
export function resolveRestaurantOpensAtLabel(restaurantInfo, referenceDate = new Date()) {
  if (!restaurantInfo) return null;
  const explicit =
    restaurantInfo.opensAtLabel ||
    restaurantInfo.schedule?.opensAtLabel ||
    restaurantInfo.schedule?.opensAt ||
    null;
  if (explicit && /^\d{1,2}:\d{2}/.test(String(explicit))) {
    return String(explicit).slice(0, 5);
  }

  const days = restaurantInfo.days;
  if (!Array.isArray(days) || !days.length) return null;

  const ref = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  if (Number.isNaN(ref.getTime())) return null;
  const weekday = weekdayKeyFromDate(ref);
  const todayConfig = days.find((d) => String(d.day || "").toLowerCase() === weekday);
  const yesterdayConfig = yesterdayConfigFromDays(days, ref);
  const status = getDayScheduleStatus(todayConfig, ref, yesterdayConfig);
  // Already open → ASAP is from now; do not clamp to today's openTime
  // (that clock is in the past and must not roll into tomorrow).
  if (status.openNow) return null;
  if (status.nextOpen && /^\d{1,2}:\d{2}/.test(status.nextOpen)) {
    return status.nextOpen.slice(0, 5);
  }
  if (!todayConfig?.isOpen || !todayConfig.openTime) return null;
  const hm = String(todayConfig.openTime).match(/^(\d{1,2}):(\d{2})/);
  if (!hm) return null;
  return `${String(hm[1]).padStart(2, "0")}:${hm[2]}`;
}

/**
 * ASAP pickup window from prep start (order time, or opening time if still closed).
 * Drive shows the slot end, which is the first 5-minute mark at or after the food is ready.
 * 17:23 + 10 min → ready 17:33. The 17:30 slot closed at 17:20, so the slot is 17:35–17:40.
 * Still closed, opens 11:00 → first slot is door open 11:00–11:05 (kitchen may prep before open).
 */
export function buildAsapPickupSlotFromOrder(orderReceived, prepMinutes, opensAtLabel = null) {
  const prep = Math.max(1, Number(prepMinutes) || 10);
  const order = orderReceived instanceof Date ? orderReceived : new Date(orderReceived);
  if (Number.isNaN(order.getTime())) return null;

  let prepStart = new Date(order.getTime());
  const opensAt = parseOpensAtInstant(opensAtLabel, order);
  const deferredToOpen = !!(opensAt && opensAt > prepStart);
  if (deferredToOpen) prepStart = opensAt;

  // Closed → first pickup at door open (matches backend future-day / before-open walk).
  // Open → food ready = now + prep, then Drive slot-end rounding.
  if (deferredToOpen) {
    const slotStart = rollAsapSlotPastCutoff(new Date(opensAt.getTime()), prep) || new Date(opensAt.getTime());
    const start = slotStart < opensAt ? new Date(opensAt.getTime()) : slotStart;
    const slotEnd = new Date(start.getTime() + 5 * 60 * 1000);
    return {
      pickupWindow: formatPickupWindowHHMM(start, slotEnd),
      date: formatSlotDate(start),
      slotStart: start,
      prepStart,
      foodReadyAt: start,
    };
  }

  const foodReadyAt = new Date(prepStart.getTime() + prep * 60 * 1000);
  const pickupAt = roundUpTo5Min(foodReadyAt);
  const roundedStart = new Date(pickupAt.getTime() - 5 * 60 * 1000);
  const slotStart = rollAsapSlotPastCutoff(roundedStart, prep) || roundedStart;
  const slotEnd = new Date(slotStart.getTime() + 5 * 60 * 1000);
  return {
    pickupWindow: formatPickupWindowHHMM(slotStart, slotEnd),
    date: formatSlotDate(slotStart),
    slotStart,
    prepStart,
    foodReadyAt,
  };
}

/**
 * Drive pickup timing per restaurant.
 * MODE 1 (departure): user leaves at order time; slot matches arrival (departure + drive).
 * MODE 1b (order now): slot is the later of kitchen-ready and drive arrival.
 * Leave = pickup time − drive, and that leave time is not already past.
 * MODE 2 (scheduled): fixed pickup slot for all; leave = pickup time − drive (varies per restaurant).
 * Pickup time is the end of the 5-minute window.
 */
export function computeDrivePickupTiming({
  orderReceivedIso,
  prepMinutes,
  driveMinutes,
  slotWindow,
  slotDate,
  mode,
  opensAtLabel = null,
  notBeforeIso = null,
  /** Drive pickup is the slot end. Walk arrives when the window opens. */
  arriveAtSlotEnd = true,
}) {
  const prep = Math.max(1, Number(prepMinutes) || 10);
  const drive = Math.max(0, Number(driveMinutes) || 0);
  const orderReceived = orderReceivedIso ? new Date(orderReceivedIso) : new Date();
  if (Number.isNaN(orderReceived.getTime())) return null;
  // A saved "leave now" time can sit in the past while the page stays open.
  // The customer cannot leave or order before the current minute.
  const wallClock = new Date();
  const earliestLeave =
    orderReceived.getTime() < wallClock.getTime() - 1000 ? wallClock : orderReceived;

  const notBefore = notBeforeIso ? new Date(notBeforeIso) : null;
  const notBeforeOk = notBefore && !Number.isNaN(notBefore.getTime()) ? notBefore : null;

  const scheduled = mode === "scheduled" && slotWindow && slotDate;
  const slotStart = scheduled
    ? parsePickupSlotInstant(slotWindow, slotDate)
    : null;

  if (scheduled && !slotStart) return null;

  let foodReady;
  let prepStart;
  let leaveBy;
  let resolvedSlotWindow = slotWindow;
  let resolvedSlotDate = slotDate;

  if (scheduled) {
    foodReady = slotStart;
    // Multi-stop chain: never book a slot before the previous pickup + dwell + leg.
    if (notBeforeOk && foodReady < notBeforeOk) {
      foodReady = roundNearest5Min(notBeforeOk);
      if (foodReady < notBeforeOk) {
        foodReady = new Date(foodReady.getTime() + 5 * 60 * 1000);
      }
      const slotEnd = new Date(foodReady.getTime() + 5 * 60 * 1000);
      resolvedSlotWindow = formatPickupWindowHHMM(foodReady, slotEnd);
      resolvedSlotDate = formatSlotDate(foodReady);
    }
    foodReady = rollAsapSlotPastCutoff(foodReady, prep, earliestLeave) || foodReady;
    prepStart = new Date(foodReady.getTime() - prep * 60 * 1000);
    leaveBy = leaveInstantForPickup(foodReady, drive, arriveAtSlotEnd);
    const pushedScheduled = pushSlotUntilLeaveIsReachable({
      foodReady,
      leaveBy,
      drive,
      prep,
      earliestLeave,
      arriveAtSlotEnd,
    });
    if (pushedScheduled) {
      foodReady = pushedScheduled.foodReady;
      prepStart = pushedScheduled.prepStart;
      leaveBy = pushedScheduled.leaveBy;
    }
    if (foodReady.getTime() !== slotStart.getTime()) {
      const slotEnd = new Date(foodReady.getTime() + PICKUP_SLOT_MS);
      resolvedSlotWindow = formatPickupWindowHHMM(foodReady, slotEnd);
      resolvedSlotDate = formatSlotDate(foodReady);
    }
  } else {
    // User leaves at the chosen departure; slot = arrival (leave + drive) on 5-min grid.
    // When leaving now, the slot is the later of kitchen-ready and drive arrival.
    leaveBy = new Date(earliestLeave.getTime());
    const arrivalAtShop = new Date(leaveBy.getTime() + drive * 60 * 1000);
    const orderingNow = earliestLeave.getTime() <= Date.now() + 60 * 1000;
    if (orderingNow) {
      const asap = buildAsapPickupSlotFromOrder(earliestLeave, prep, opensAtLabel);
      if (asap) {
        foodReady = asap.slotStart;
        prepStart = asap.prepStart;
        resolvedSlotWindow = asap.pickupWindow;
        resolvedSlotDate = asap.date;
        leaveBy = leaveInstantForPickup(foodReady, drive, arriveAtSlotEnd);
      } else {
        foodReady = roundDownTo5Min(arrivalAtShop);
        prepStart = new Date(foodReady.getTime() - prep * 60 * 1000);
        const slotEnd = new Date(foodReady.getTime() + 5 * 60 * 1000);
        resolvedSlotWindow = formatPickupWindowHHMM(foodReady, slotEnd);
        resolvedSlotDate = formatSlotDate(foodReady);
      }
    } else {
      foodReady = roundDownTo5Min(arrivalAtShop);
      prepStart = new Date(foodReady.getTime() - prep * 60 * 1000);
      const slotEnd = new Date(foodReady.getTime() + 5 * 60 * 1000);
      resolvedSlotWindow = formatPickupWindowHHMM(foodReady, slotEnd);
      resolvedSlotDate = formatSlotDate(foodReady);
    }

    if (notBeforeOk && foodReady < notBeforeOk) {
      foodReady = roundNearest5Min(notBeforeOk);
      if (foodReady < notBeforeOk) {
        foodReady = new Date(foodReady.getTime() + 5 * 60 * 1000);
      }
      foodReady = rollAsapSlotPastCutoff(foodReady, prep) || foodReady;
      prepStart = new Date(foodReady.getTime() - prep * 60 * 1000);
      leaveBy = leaveInstantForPickup(foodReady, drive, arriveAtSlotEnd);
      const slotEnd = new Date(foodReady.getTime() + 5 * 60 * 1000);
      resolvedSlotWindow = formatPickupWindowHHMM(foodReady, slotEnd);
      resolvedSlotDate = formatSlotDate(foodReady);
    }

    if (orderingNow) {
      const pushed = pushSlotUntilLeaveIsReachable({
        foodReady,
        leaveBy,
        drive,
        prep,
        earliestLeave,
        arriveAtSlotEnd,
      });
      if (pushed) {
        foodReady = pushed.foodReady;
        prepStart = pushed.prepStart;
        leaveBy = pushed.leaveBy;
        const slotEnd = new Date(foodReady.getTime() + PICKUP_SLOT_MS);
        resolvedSlotWindow = formatPickupWindowHHMM(foodReady, slotEnd);
        resolvedSlotDate = formatSlotDate(foodReady);
      }
    }
  }

  const slotLabel = resolvedSlotWindow
    ? `${formatTravelTimeLabel(foodReady)}\u2013${formatTravelTimeLabel(new Date(foodReady.getTime() + 5 * 60 * 1000))}`
    : formatTravelTimeLabel(foodReady);

  const resolvedKitchenStartLabel = formatTravelTimeLabel(prepStart);
  // Same kitchen cutoff as takeaway and dine-in. Leave by is the drive time.
  const orderBeforeAt = computeSlotBookingCutoffAt(foodReady, prep) || prepStart;

  return {
    mode: scheduled ? "scheduled" : "asap",
    orderReceivedLabel: formatTravelTimeLabel(orderReceived),
    prepMinutes: prep,
    driveMinutes: drive,
    prepStartLabel: resolvedKitchenStartLabel,
    orderBeforeLabel: formatTravelTimeLabel(orderBeforeAt),
    leaveByLabel: formatTravelTimeLabel(leaveBy),
    leaveByAt: leaveBy.toISOString(),
    foodReadyLabel: formatTravelTimeLabel(foodReady),
    arriveByLabel: formatTravelTimeLabel(new Date(leaveBy.getTime() + drive * 60 * 1000)),
    slotLabel,
    pickupWindow: resolvedSlotWindow || null,
    date: resolvedSlotDate || null,
  };
}

/**
 * Route summary when a pickup slot filter is active (scheduled mode check).
 */
export function computeTravelSlotTiming({
  selectedSlot,
  shops,
  userDepartureIso,
}) {
  if (!selectedSlot?.pickupWindow || !selectedSlot?.date) return null;

  const allShops = [...(shops?.boulangeries || []), ...(shops?.restaurants || [])];
  const matching = allShops.filter((s) =>
    s.smartRoute?.bookableSlots?.some(
      (slot) =>
        slot.pickupWindow === selectedSlot.pickupWindow &&
        slot.date === selectedSlot.date &&
        slot.hasCapacity,
    ),
  );
  if (!matching.length) return null;

  const shop = [...matching].sort(
    (a, b) => (a.smartRoute?.matchScore ?? Infinity) - (b.smartRoute?.matchScore ?? Infinity),
  )[0];

  const timing = computeDrivePickupTiming({
    orderReceivedIso: userDepartureIso,
    prepMinutes: shop.prepMinutes,
    driveMinutes: getDriveMinutesFromDeparture(shop),
    slotWindow: selectedSlot.pickupWindow,
    slotDate: selectedSlot.date,
    mode: "scheduled",
  });
  if (!timing) return null;

  const userDeparture = userDepartureIso ? new Date(userDepartureIso) : new Date();
  const slotStart = parsePickupSlotInstant(selectedSlot.pickupWindow, selectedSlot.date);
  const arrivalAtShop = new Date(userDeparture.getTime() + timing.driveMinutes * 60 * 1000);
  const pickupAt = slotStart ? new Date(slotStart.getTime() + PICKUP_SLOT_MS) : null;
  const waitMin = pickupAt
    ? Math.round((pickupAt.getTime() - arrivalAtShop.getTime()) / 60000)
    : 0;

  let status = "ok";
  if (waitMin < 0) status = "late";
  else if (waitMin > COMFORTABLE_WAIT_MIN) status = "tooEarly";

  const bestMatch = shop.smartRoute?.bestMatchSlot;
  const selectedKey = slotKey(selectedSlot.pickupWindow, selectedSlot.date);
  const bestKey = bestMatch ? slotKey(bestMatch.pickupWindow, bestMatch.date) : "";
  let betterSlot = null;
  if (bestMatch && bestKey !== selectedKey && status !== "ok") {
    betterSlot = {
      pickupWindow: bestMatch.pickupWindow,
      date: bestMatch.date,
      label: formatPickupWindowLabel(bestMatch.pickupWindow),
      leaveByLabel: timing.leaveByLabel,
    };
  }

  return {
    ...timing,
    slotLabel: timing.slotLabel,
    shopName: shop.shopDetails?.shopName || "",
    idealLeaveByLabel: timing.leaveByLabel,
    userDepartureLabel: formatTravelTimeLabel(userDeparture),
    kitchenStartLabel: timing.prepStartLabel,
    routeOffsetMin: timing.driveMinutes,
    waitMin: Math.max(0, waitMin),
    lateMin: waitMin < 0 ? Math.abs(waitMin) : 0,
    status,
    betterSlot,
  };
}

/** Recommended leave time for a fixed pickup slot (pickup time − drive). */
export function computeLeaveByForPickupSlot(pickupWindow, date, driveMinutes) {
  const slotStart = parsePickupSlotInstant(pickupWindow, date);
  if (!slotStart) return null;
  return formatTravelTimeLabel(leaveInstantForPickup(slotStart, driveMinutes));
}

/** ISO leave time so the customer arrives at the pickup (slot end). */
export function leaveIsoForPickupSlot(pickupWindow, date, driveMinutes) {
  const slotStart = parsePickupSlotInstant(pickupWindow, date);
  if (!slotStart) return null;
  return leaveInstantForPickup(slotStart, driveMinutes, true).toISOString();
}

/** True when two slot labels are the same start, ignoring dash style. */
export function samePickupSlot(windowA, dateA, windowB, dateB) {
  const a = parsePickupSlotInstant(windowA, dateA);
  const b = parsePickupSlotInstant(windowB, dateB);
  if (a && b) return a.getTime() === b.getTime();
  return String(windowA || "") === String(windowB || "") && String(dateA || "") === String(dateB || "");
}

/** Per-restaurant explore card timing (Drive = car, Near Me = walk). */
export function computeShopTravelCardTiming(
  shop,
  selectedTravelPickupSlot = null,
  userDepartureIso = null,
  { travelMode = "drive" } = {},
) {
  if (!shop) return null;

  const mode = travelMode === "walk" ? "walk" : "drive";
  const sr = shop.smartRoute || null;
  const estimates = getWalkDriveMinutes(shop);
  const routedDrive = getDriveMinutesFromDeparture(shop);
  const driveTravelMinutes = routedDrive > 0 ? routedDrive : estimates.drive || 0;
  const walkMinutes = estimates.walk || 0;
  const travelMinutes = mode === "walk" ? walkMinutes : driveTravelMinutes;
  const isScheduled = !!(
    selectedTravelPickupSlot?.pickupWindow && selectedTravelPickupSlot?.date
  );

  const schedule = sr?.schedule || shop.schedule;
  const restaurantClosed =
    schedule && !schedule.manualClosed && schedule.openNow !== true;

  const backendSlot = (() => {
    if (sr?.bestMatchSlot?.pickupWindow && sr?.bestMatchSlot?.date) return sr.bestMatchSlot;
    if (shop.nextSlot?.pickupWindow && shop.nextSlot?.date) return shop.nextSlot;
    return (
      sr?.bookableSlots?.find((s) => s.hasCapacity && s.pickupWindow && s.date) || null
    );
  })();

  const isExplicitPreOrder = (() => {
    if (sr?.isPreOrder === true) return true;
    if (!userDepartureIso) return false;
    const departure = new Date(userDepartureIso);
    if (Number.isNaN(departure.getTime())) return false;
    return departure.getTime() - Date.now() > 15 * 60 * 1000;
  })();

  // Browsing without a future leave time while closed → anchor to next bookable slot.
  const useSlotAnchored =
    !isScheduled && !isExplicitPreOrder && restaurantClosed && backendSlot;

  // Closed past tonight with no next-day slot from the API → don't invent a midnight ASAP créneau.
  if (
    restaurantClosed &&
    !backendSlot &&
    !isScheduled &&
    !isExplicitPreOrder &&
    !schedule?.opensAtLabel
  ) {
    return null;
  }

  let slotWindow = null;
  let slotDate = null;

  if (isScheduled) {
    const picked =
      sr?.bookableSlots?.find(
        (s) =>
          s.pickupWindow === selectedTravelPickupSlot.pickupWindow &&
          s.date === selectedTravelPickupSlot.date &&
          s.hasCapacity,
      ) ||
      (backendSlot &&
      backendSlot.pickupWindow === selectedTravelPickupSlot.pickupWindow &&
      backendSlot.date === selectedTravelPickupSlot.date
        ? backendSlot
        : null);
    if (!picked) return null;
    slotWindow = picked.pickupWindow;
    slotDate = picked.date;
  } else if (useSlotAnchored) {
    slotWindow = backendSlot.pickupWindow;
    slotDate = backendSlot.date;
  }

  const timing = computeDrivePickupTiming({
    orderReceivedIso: userDepartureIso,
    prepMinutes: shop.prepMinutes,
    driveMinutes: travelMinutes,
    slotWindow,
    slotDate,
    mode: isScheduled || useSlotAnchored ? "scheduled" : "asap",
    opensAtLabel: schedule?.opensAtLabel || null,
    arriveAtSlotEnd: mode !== "walk",
  });

  if (!timing) return null;

  const seats = resolveSlotSeatCount(shop, timing.pickupWindow, timing.date, backendSlot);

  return {
    ...timing,
    travelMode: mode,
    driveMinutes: travelMinutes,
    walkMinutes,
    driveTravelMinutes,
    slotRemainingOrders: seats.remaining,
    slotSeatLimit: seats.limit,
  };
}

/** Prefer remaining seats for the timed slot; fall back so cards always show (N). */
function resolveSlotSeatCount(shop, pickupWindow, date, backendSlot = null) {
  const sr = shop?.smartRoute || null;
  const candidates = [];

  const push = (slot) => {
    if (!slot) return;
    candidates.push(slot);
  };

  if (pickupWindow && date) {
    push(
      sr?.bookableSlots?.find(
        (s) => s.pickupWindow === pickupWindow && s.date === date,
      ),
    );
    if (
      shop.nextSlot?.pickupWindow === pickupWindow &&
      shop.nextSlot?.date === date
    ) {
      push(shop.nextSlot);
    }
    if (
      sr?.bestMatchSlot?.pickupWindow === pickupWindow &&
      sr?.bestMatchSlot?.date === date
    ) {
      push(sr.bestMatchSlot);
    }
  }

  // ASAP window may not match Redis keys exactly — still use the shop's live next/best slot.
  push(backendSlot);
  push(sr?.bestMatchSlot);
  push(shop.nextSlot);
  push(sr?.bookableSlots?.find((s) => s.hasCapacity && typeof s.slotRemainingOrders === "number"));
  push(sr?.bookableSlots?.find((s) => typeof s.slotRemainingOrders === "number"));

  let remaining = null;
  let limit = null;
  for (const slot of candidates) {
    if (remaining == null && typeof slot.slotRemainingOrders === "number") {
      remaining = slot.slotRemainingOrders;
    }
    if (limit == null && typeof slot.slotSeatLimit === "number") {
      limit = slot.slotSeatLimit;
    }
    if (remaining != null && limit != null) break;
  }

  if (remaining == null && typeof limit === "number") remaining = limit;

  return { remaining, limit };
}

function slotAtOrAfterNotBefore(pickupWindow, date, notBeforeIso) {
  if (!notBeforeIso) return true;
  const start = parsePickupSlotInstant(pickupWindow, date);
  const floor = new Date(notBeforeIso);
  if (!start || Number.isNaN(floor.getTime())) return true;
  return start.getTime() >= floor.getTime() - 45 * 1000;
}

function slotAtOrBeforeNotAfter(pickupWindow, date, notAfterIso) {
  if (!notAfterIso) return true;
  const start = parsePickupSlotInstant(pickupWindow, date);
  const ceil = new Date(notAfterIso);
  if (!start || Number.isNaN(ceil.getTime())) return true;
  return start.getTime() <= ceil.getTime() + 45 * 1000;
}

function windowFromSlotStart(slotStart) {
  const end = new Date(slotStart.getTime() + 5 * 60 * 1000);
  return {
    pickupWindow: formatPickupWindowHHMM(slotStart, end),
    date: formatSlotDate(slotStart),
  };
}

/** Move a chosen slot forward when its order-by minute has already passed for this prep. */
function advanceSlotPastBookingCutoff(slotWindow, slotDate, prepMinutes, bookableSlots) {
  const start = parsePickupSlotInstant(slotWindow, slotDate);
  if (!start || !isSlotPastBookingCutoff(start, prepMinutes)) {
    return { pickupWindow: slotWindow, date: slotDate };
  }
  const rolled = rollAsapSlotPastCutoff(start, prepMinutes);
  if (!rolled) return { pickupWindow: slotWindow, date: slotDate };
  const rolledSlot = windowFromSlotStart(rolled);
  const open = (Array.isArray(bookableSlots) ? bookableSlots : [])
    .filter((s) => s?.hasCapacity && s.pickupWindow && s.date)
    .sort((a, b) => `${a.date}|${a.pickupWindow}`.localeCompare(`${b.date}|${b.pickupWindow}`));
  const exact = open.find(
    (s) => s.pickupWindow === rolledSlot.pickupWindow && s.date === rolledSlot.date,
  );
  if (exact) return { pickupWindow: exact.pickupWindow, date: exact.date };
  const later = open.find((s) => {
    const slotStart = parsePickupSlotInstant(s.pickupWindow, s.date);
    return slotStart && slotStart.getTime() >= rolled.getTime() - 45 * 1000;
  });
  if (later) return { pickupWindow: later.pickupWindow, date: later.date };
  return rolledSlot;
}

function earliestBookableAfter(bookableSlots, notBeforeIso, notAfterIso = null) {
  const slots = (Array.isArray(bookableSlots) ? bookableSlots : [])
    .filter((s) => s?.hasCapacity && s.pickupWindow && s.date)
    .sort((a, b) => `${a.date}|${a.pickupWindow}`.localeCompare(`${b.date}|${b.pickupWindow}`));
  if (!slots.length) return null;
  return (
    slots.find(
      (s) =>
        slotAtOrAfterNotBefore(s.pickupWindow, s.date, notBeforeIso) &&
        slotAtOrBeforeNotAfter(s.pickupWindow, s.date, notAfterIso),
    ) || null
  );
}

/** Auto slot that was rounded up one window (17:35–17:40) while 17:30–17:35 is still bookable. */
function alignAutoDriveSlotToReadyWindow({
  slotWindow,
  slotDate,
  prepMinutes,
  hint,
  selectedSlot,
  bookableSlots = [],
}) {
  const autoSelected =
    selectedSlot &&
    hint?.pickupWindow === selectedSlot.pickupWindow &&
    String(hint?.pickupDate || "") === String(selectedSlot.date || "");
  if (!autoSelected) return null;
  const asap = buildAsapPickupSlotFromOrder(new Date(), prepMinutes);
  if (!asap?.slotStart || !asap.pickupWindow) return null;
  const chosen = parsePickupSlotInstant(slotWindow, slotDate);
  if (!chosen) return null;
  if (chosen.getTime() - asap.slotStart.getTime() !== 5 * 60 * 1000) return null;
  const openSlots = (Array.isArray(bookableSlots) ? bookableSlots : []).filter(
    (s) => s?.hasCapacity && s.pickupWindow && s.date,
  );
  // A full or missing earlier window must stay behind the slot we can actually reserve.
  if (
    openSlots.length &&
    !openSlots.some(
      (s) => s.pickupWindow === asap.pickupWindow && String(s.date) === String(asap.date),
    )
  ) {
    return null;
  }
  return { pickupWindow: asap.pickupWindow, date: asap.date };
}

/** How far ahead a listed slot can replace a computed window that is not actually open. */
const NEARBY_BOOKABLE_SNAP_MS = 15 * 60 * 1000;

/**
 * Leave-now can compute 11:05–11:10 (shown as pickup 11:10) while that window
 * is full or not offered. Use the next listed slot in the next few minutes.
 * A listed slot much later (stale explore hint) does not replace a live ASAP window.
 */
function snapToNearbyBookableSlot(slotWindow, slotDate, bookableSlots, notAfterIso = null) {
  const slots = (Array.isArray(bookableSlots) ? bookableSlots : [])
    .filter((s) => s?.hasCapacity && s.pickupWindow && s.date)
    .sort((a, b) => `${a.date}|${a.pickupWindow}`.localeCompare(`${b.date}|${b.pickupWindow}`));
  if (!slots.length) return null;
  const exact = slots.find(
    (s) => s.pickupWindow === slotWindow && String(s.date) === String(slotDate),
  );
  if (exact) return exact;
  const start = parsePickupSlotInstant(slotWindow, slotDate);
  if (!start) return null;
  return (
    slots.find((s) => {
      const slotStart = parsePickupSlotInstant(s.pickupWindow, s.date);
      if (!slotStart) return false;
      const delta = slotStart.getTime() - start.getTime();
      return (
        delta >= -45 * 1000 &&
        delta <= NEARBY_BOOKABLE_SNAP_MS &&
        slotAtOrBeforeNotAfter(s.pickupWindow, s.date, notAfterIso)
      );
    }) || null
  );
}

function retargetTimingToNearbyBookableSlot({
  pickupWindow,
  pickupDate,
  timing,
  bookableSlots,
  notAfterIso,
  orderReceivedIso,
  prepMinutes,
  driveMinutes,
  opensAtLabel,
  notBeforeIso,
}) {
  const listed = snapToNearbyBookableSlot(pickupWindow, pickupDate, bookableSlots, notAfterIso);
  if (!listed || samePickupSlot(listed.pickupWindow, listed.date, pickupWindow, pickupDate)) {
    return { pickupWindow, pickupDate, timing };
  }
  const listedTiming = computeDrivePickupTiming({
    orderReceivedIso,
    prepMinutes,
    driveMinutes,
    slotWindow: listed.pickupWindow,
    slotDate: listed.date,
    mode: "scheduled",
    opensAtLabel,
    notBeforeIso,
  });
  if (!listedTiming?.pickupWindow) return { pickupWindow, pickupDate, timing };
  return {
    pickupWindow: listedTiming.pickupWindow,
    pickupDate: listedTiming.date,
    timing: listedTiming,
  };
}

/** Drive checkout plan: resolved slot + timing from session hint and live filters. */
export function resolveDriveCheckoutPlan({
  hint,
  restaurantInfo,
  departureLocalValue = "",
  leaveNowMode = true,
  userDepartureIso: userDepartureIsoLegacy = null,
  slotFilterKey = "",
}) {
  if (!hint?.pickupWindow || !hint?.pickupDate) return null;

  const prep = Math.max(1, Number(hint.prepMinutes) || Number(restaurantInfo?.prepMinutes) || 10);
  const drive = Math.max(0, Number(hint.driveMinutes) || 0);
  const notBeforeIso = hint.notBeforeIso || null;
  const notAfterIso = hint.notAfterIso || null;
  const bookableSlots = Array.isArray(hint.bookableSlots) ? hint.bookableSlots : [];
  const selectedSlot = parseTravelSlotFilterKey(slotFilterKey);
  const orderReceivedIso = leaveNowMode
    ? userDepartureIsoLegacy || resolveDepartureIso(departureLocalValue, true)
    : resolveDepartureIso(departureLocalValue, false);
  const showFullDetails = !!(selectedSlot || !leaveNowMode);
  const opensAtLabel = resolveRestaurantOpensAtLabel(
    restaurantInfo,
    orderReceivedIso ? new Date(orderReceivedIso) : new Date(),
  );

  const seatMetaFor = (pickupWindow, date) => {
    const match = bookableSlots.find(
      (s) => s.pickupWindow === pickupWindow && s.date === date && s.hasCapacity,
    );
    const fromMatch =
      typeof match?.slotRemainingOrders === "number" ? match.slotRemainingOrders : null;
    // Fallback when bookableSlots lack a row (hold sync still writes hint.slotRemainingOrders).
    const fromHint =
      samePickupSlot(pickupWindow, date, hint.pickupWindow, hint.pickupDate) &&
      typeof hint.slotRemainingOrders === "number"
        ? hint.slotRemainingOrders
        : null;
    return {
      slotRemainingOrders: fromMatch ?? fromHint,
    };
  };

  const slotInChainWindow = (pickupWindow, date) =>
    slotAtOrAfterNotBefore(pickupWindow, date, notBeforeIso) &&
    slotAtOrBeforeNotAfter(pickupWindow, date, notAfterIso);

  if (selectedSlot) {
    let picked = bookableSlots.find(
      (s) =>
        s.pickupWindow === selectedSlot.pickupWindow &&
        s.date === selectedSlot.date &&
        s.hasCapacity,
    );
    // If the chosen filter is outside the multi-stop chain window, bump to a valid slot.
    if (picked && !slotInChainWindow(picked.pickupWindow, picked.date)) {
      picked = earliestBookableAfter(bookableSlots, notBeforeIso, notAfterIso);
    }
    let slotWindowRaw = picked?.pickupWindow ?? selectedSlot.pickupWindow;
    let slotDateRaw = picked?.date ?? selectedSlot.date;
    if (!picked) {
      const listed = snapToNearbyBookableSlot(
        slotWindowRaw,
        slotDateRaw,
        bookableSlots,
        notAfterIso,
      );
      if (listed) {
        slotWindowRaw = listed.pickupWindow;
        slotDateRaw = listed.date;
      }
    }
    const aligned = alignAutoDriveSlotToReadyWindow({
      slotWindow: slotWindowRaw,
      slotDate: slotDateRaw,
      prepMinutes: prep,
      hint,
      selectedSlot,
      bookableSlots,
    });
    const alignedWindow = aligned?.pickupWindow || slotWindowRaw;
    const alignedDate = aligned?.date || slotDateRaw;
    // Cart prep can be longer than the explore slot (restaurant default).
    // A locked 22:50 window is already past its order-by time for a 15-minute item.
    const advanced = advanceSlotPastBookingCutoff(
      alignedWindow,
      alignedDate,
      prep,
      bookableSlots,
    );
    const slotWindow = advanced.pickupWindow;
    const slotDate = advanced.date;

    const timing = computeDrivePickupTiming({
      orderReceivedIso,
      prepMinutes: prep,
      driveMinutes: drive,
      slotWindow,
      slotDate,
      mode: "scheduled",
      opensAtLabel,
      notBeforeIso,
    });
    if (!timing) return null;

    // If ASAP/scheduled bump pushed past the next further stop, prefer a chained bookable slot.
    if (
      notAfterIso &&
      timing.pickupWindow &&
      timing.date &&
      !slotAtOrBeforeNotAfter(timing.pickupWindow, timing.date, notAfterIso)
    ) {
      const chained = earliestBookableAfter(bookableSlots, notBeforeIso, notAfterIso);
      if (chained) {
        const chainedTiming = computeDrivePickupTiming({
          orderReceivedIso,
          prepMinutes: prep,
          driveMinutes: drive,
          slotWindow: chained.pickupWindow,
          slotDate: chained.date,
          mode: "scheduled",
          opensAtLabel,
          notBeforeIso,
        });
        if (chainedTiming) {
          const seats = seatMetaFor(chainedTiming.pickupWindow, chainedTiming.date);
          return {
            pickupWindow: chainedTiming.pickupWindow,
            pickupDate: chainedTiming.date,
            timingMode: "scheduled",
            showFullDetails,
            timing: { ...chainedTiming, ...seats, showFullDetails },
          };
        }
      }
    }

    const seats = seatMetaFor(timing.pickupWindow || slotWindow, timing.date || slotDate);
    return {
      pickupWindow: timing.pickupWindow || slotWindow,
      pickupDate: timing.date || slotDate,
      timingMode: "scheduled",
      showFullDetails,
      timing: { ...timing, ...seats, showFullDetails },
    };
  }

  const restaurantClosed = restaurantInfo?.restaurantOpen === false;
  const hasSchedule =
    !!opensAtLabel ||
    (Array.isArray(restaurantInfo?.days) && restaurantInfo.days.length > 0);
  // Before hours are known, keep the explore slot (e.g. 11:10) instead of now+prep (04:05 at 4am).
  const useHintUntilSchedule =
    leaveNowMode && !hasSchedule && hint.pickupWindow && hint.pickupDate;
  const useAnchoredSlot =
    (restaurantClosed && bookableSlots.length > 0) || useHintUntilSchedule;
  const anchored = useAnchoredSlot
    ? earliestBookableAfter(bookableSlots, notBeforeIso, notAfterIso) ||
      bookableSlots.find((s) => s.hasCapacity && s.pickupWindow && s.date) ||
      (useHintUntilSchedule
        ? { pickupWindow: hint.pickupWindow, date: hint.pickupDate }
        : null)
    : null;

  const timing = computeDrivePickupTiming({
    orderReceivedIso,
    prepMinutes: prep,
    driveMinutes: drive,
    slotWindow: anchored ? anchored.pickupWindow : null,
    slotDate: anchored ? anchored.date : null,
    mode: anchored ? "scheduled" : "asap",
    opensAtLabel,
    notBeforeIso,
  });
  if (!timing?.pickupWindow || !timing?.date) return null;

  // Prefer a real capacity slot inside the chained window when available.
  let pickupWindow = timing.pickupWindow;
  let pickupDate = timing.date;
  if ((notBeforeIso || notAfterIso) && bookableSlots.length) {
    const chained = earliestBookableAfter(bookableSlots, notBeforeIso, notAfterIso);
    if (
      chained &&
      (!slotInChainWindow(pickupWindow, pickupDate) ||
        `${chained.date}|${chained.pickupWindow}` < `${pickupDate}|${pickupWindow}`)
    ) {
      const chainedTiming = computeDrivePickupTiming({
        orderReceivedIso,
        prepMinutes: prep,
        driveMinutes: drive,
        slotWindow: chained.pickupWindow,
        slotDate: chained.date,
        mode: "scheduled",
        opensAtLabel,
        notBeforeIso,
      });
      if (chainedTiming?.pickupWindow) {
        pickupWindow = chainedTiming.pickupWindow;
        pickupDate = chainedTiming.date;
        const seats = seatMetaFor(pickupWindow, pickupDate);
        return {
          pickupWindow,
          pickupDate,
          timingMode: showFullDetails ? "departure" : "default",
          showFullDetails,
          timing: { ...chainedTiming, ...seats, showFullDetails },
        };
      }
    }
  }

  const listed = retargetTimingToNearbyBookableSlot({
    pickupWindow,
    pickupDate,
    timing,
    bookableSlots,
    notAfterIso,
    orderReceivedIso,
    prepMinutes: prep,
    driveMinutes: drive,
    opensAtLabel,
    notBeforeIso,
  });
  const seats = seatMetaFor(listed.pickupWindow, listed.pickupDate);

  return {
    pickupWindow: listed.pickupWindow,
    pickupDate: listed.pickupDate,
    timingMode: showFullDetails ? (selectedSlot ? "scheduled" : "departure") : "default",
    showFullDetails,
    timing: { ...listed.timing, ...seats, showFullDetails },
  };
}

/** @deprecated Use resolveDriveCheckoutPlan */
export function resolveDriveCheckoutTiming(travelSlotHint, restaurantInfo) {
  return resolveDriveCheckoutPlan({
    hint: travelSlotHint,
    restaurantInfo,
    userDepartureIso: travelSlotHint?.userDepartureIso,
    slotFilterKey:
      travelSlotHint?.timingMode === "scheduled"
        ? `${travelSlotHint.pickupWindow}|${travelSlotHint.pickupDate}`
        : "",
  })?.timing;
}

/**
 * Build API payload for drive pickup timing snapshot (checkout hold).
 */
export function buildDrivePickupTimingSnapshot(timing) {
  if (!timing?.orderBeforeLabel) return null;
  const remaining = timing.slotRemainingOrders;
  const remainingNum = Number(remaining);
  return {
    orderBeforeLabel: timing.orderBeforeLabel,
    prepMinutes: timing.prepMinutes,
    leaveByLabel: timing.leaveByLabel,
    driveMinutes: timing.driveMinutes,
    slotLabel: timing.slotLabel,
    slotRemainingOrders:
      remaining != null &&
      remaining !== "" &&
      Number.isInteger(remainingNum) &&
      remainingNum >= 0
        ? remainingNum
        : null,
  };
}

/**
 * Reconstruct drive pickup display from order snapshot fields (order confirmation page).
 */
export function buildDriveOrderDisplayTiming(order) {
  if (!order) return null;

  const hasDrivePlan =
    order.driveLeaveHomeAt ||
    order.drivePickupTimingSnapshot?.orderBeforeLabel ||
    order.drivePickupTimingSnapshot?.leaveByLabel ||
    String(order.orderType || "").toUpperCase() === "DRIVE";
  if (!hasDrivePlan) return null;

  const snap = order.drivePickupTimingSnapshot;
  if (snap?.orderBeforeLabel) {
    const remaining = snap.slotRemainingOrders;
    const remainingNum = Number(remaining);
    return {
      orderBeforeLabel: snap.orderBeforeLabel,
      prepMinutes: Math.max(0, Number(snap.prepMinutes) || 0),
      leaveByLabel: snap.leaveByLabel || null,
      driveMinutes: Math.max(0, Number(snap.driveMinutes) || 0),
      slotLabel: snap.slotLabel || formatPickupWindowLabel(order.pickupWindow),
      slotDate: order.pickupDate || null,
      travelMode: "drive",
      slotRemainingOrders:
        remaining != null &&
        remaining !== "" &&
        Number.isInteger(remainingNum) &&
        remainingNum >= 0
          ? remainingNum
          : null,
    };
  }

  const leaveAt = order.driveLeaveHomeAt ? new Date(order.driveLeaveHomeAt) : null;
  const slotWindow = order.pickupWindow;
  const slotDate = order.pickupDate;
  const slotStart = parsePickupSlotInstant(slotWindow, slotDate);

  let driveMinutes = 0;
  if (leaveAt && slotStart && !Number.isNaN(leaveAt.getTime())) {
    driveMinutes = Math.max(0, Math.round((slotStart.getTime() - leaveAt.getTime()) / 60000));
  }

  const leaveByLabel = leaveAt && !Number.isNaN(leaveAt.getTime()) ? formatTravelTimeLabel(leaveAt) : null;
  const slotLabel = slotWindow ? formatPickupWindowLabel(slotWindow) : null;
  const orderReceived = order.confirmedAt || order.createdAt;
  const orderReceivedDate = orderReceived ? new Date(orderReceived) : null;
  const orderReceivedLabel =
    orderReceivedDate && !Number.isNaN(orderReceivedDate.getTime())
      ? formatTravelTimeLabel(orderReceivedDate)
      : null;

  if (!leaveByLabel && !slotLabel) return null;

  return {
    orderBeforeLabel: orderReceivedLabel,
    prepMinutes: 0,
    leaveByLabel: leaveByLabel || "—",
    driveMinutes,
    slotLabel: slotLabel || "—",
    slotDate: slotDate || null,
    orderReceivedLabel,
    arriveByLabel: slotStart ? formatTravelTimeLabel(slotStart) : null,
  };
}
